<template>
  <v-container>
    <h1>User Profile</h1>

    <!-- Alias with inline edit -->
    <div class="d-flex align-center mb-1" style="gap: 12px;">
      <template v-if="!editingAlias">
        <span><strong>Alias:</strong> {{ username }}</span>
        <v-btn icon size="x-small" variant="text" title="Change alias" @click="startEditAlias">
          <v-icon size="small">mdi-pencil</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-text-field
          v-model="aliasInput"
          label="New alias"
          density="compact"
          hide-details
          style="max-width: 220px;"
        />
        <v-btn size="small" color="primary" :loading="aliasSaving" @click="saveAlias">Save</v-btn>
        <v-btn size="small" variant="text" @click="cancelEditAlias">Cancel</v-btn>
      </template>
    </div>
    <p v-if="aliasFeedback" class="text-caption mb-2" :class="aliasFeedbackError ? 'text-error' : 'text-success'">{{ aliasFeedback }}</p>

    <p>Email: {{ useremail }}</p>
    <p>Admin: {{ admin }}</p>
    <p>Favorite Places: {{ favoriteplaces }}</p>
    <p>Favorite Games: {{ favoritegames }}</p>

    <v-row class="mt-4">
      <!-- People who can add me to tournaments -->
      <v-col cols="12" md="6">
        <v-card class="pa-4 h-100">
          <v-card-title>People who can add me to tournaments</v-card-title>
          <v-card-subtitle>
            These usernames (aliases) can enter you in tournaments when the tournament is tracking players by name.
            <template v-if="hasPokerPermission"> Tournament results will sync to your session list when they save results.</template>
            <template v-else> Your name will appear on their saved results; no session is created for you since you're not using Poker Session Tracker.</template>
          </v-card-subtitle>
          <v-card-text>
            <div class="d-flex align-center mb-3" style="gap: 8px;">
              <v-text-field
                v-model="newEntererAlias"
                label="Username (alias)"
                placeholder="Enter another user's username"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1"
                @keydown.enter.prevent="addEnterer"
              />
              <v-btn color="primary" :disabled="!newEntererAliasTrimmed" @click="addEnterer">
                Add
              </v-btn>
            </div>
            <p v-if="entererFeedback" class="text-caption" :class="entererFeedbackError ? 'text-error' : 'text-medium-emphasis'">
              {{ entererFeedback }}
            </p>
            <v-list v-if="resolvedEnterers.length > 0" density="compact" class="bg-transparent">
              <v-list-item
                v-for="item in resolvedEnterers"
                :key="item.uid"
                class="px-0"
              >
                <template #prepend>
                  <v-icon size="small">mdi-account</v-icon>
                </template>
                <v-list-item-title>{{ item.alias }}</v-list-item-title>
                <template #append>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    color="error"
                    title="Remove"
                    @click="removeEnterer(item.uid)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
            <p v-else class="text-body-2 text-medium-emphasis">
              No one added yet. Add usernames of people who may run tournaments and enter you by name.
            </p>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- People I can add to tournaments -->
      <v-col cols="12" md="6">
        <v-card class="pa-4 h-100">
          <v-card-title>People I can add to tournaments</v-card-title>
          <v-card-subtitle>
            These users have granted you permission to enter them in tournaments by name.
          </v-card-subtitle>
          <v-card-text>
            <div v-if="grantedAccessLoading" class="d-flex justify-center py-4">
              <v-progress-circular indeterminate color="primary" size="24" />
            </div>
            <template v-else>
              <v-list v-if="grantedAccess.length > 0" density="compact" class="bg-transparent">
                <v-list-item
                  v-for="person in grantedAccess"
                  :key="person.user_id"
                  class="px-0"
                >
                  <template #prepend>
                    <v-icon size="small">mdi-account</v-icon>
                  </template>
                  <v-list-item-title>{{ person.alias }}</v-list-item-title>
                  <template #append>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      color="error"
                      title="Remove access"
                      @click="confirmBlock(person)"
                    >
                      <v-icon>mdi-account-remove</v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
              </v-list>
              <p v-else class="text-body-2 text-medium-emphasis">
                No one has granted you access yet.
              </p>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Place Groups -->
    <v-card class="pa-2 mt-6">
      <v-card-title class="d-flex justify-space-between align-center">
        Place Groups
        <v-btn size="small" color="primary" variant="tonal" @click="openAddGroup">+ Add Group</v-btn>
      </v-card-title>
      <v-card-subtitle>Group your places to filter and aggregate reports by group.</v-card-subtitle>
      <v-card-text>
        <p v-if="placeGroups.length === 0" class="text-body-2 text-medium-emphasis">No groups yet. Add a group to get started.</p>
        <v-list density="compact" class="bg-transparent">
          <v-list-item v-for="(group, i) in placeGroups" :key="group.name" class="px-0">
            <v-list-item-title class="font-weight-medium">{{ group.name }}</v-list-item-title>
            <v-list-item-subtitle>
              <v-chip v-for="p in group.places" :key="p" size="x-small" class="mr-1 mt-1">{{ p }}</v-chip>
              <span v-if="group.places.length === 0" class="text-medium-emphasis text-caption">No places added</span>
            </v-list-item-subtitle>
            <template #append>
              <v-btn icon size="small" variant="text" title="Edit" @click="openEditGroup(i)">
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon size="small" variant="text" color="error" title="Delete" @click="deleteGroupIndex = i">
                <v-icon size="small">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
        <div v-if="deleteGroupIndex >= 0" class="mt-3 d-flex align-center" style="gap: 8px;">
          <span class="text-body-2">Delete <strong>{{ placeGroups[deleteGroupIndex]?.name }}</strong>?</span>
          <v-btn size="small" color="error" variant="tonal" @click="deleteGroup">Delete</v-btn>
          <v-btn size="small" variant="text" @click="deleteGroupIndex = -1">Cancel</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Group dialog -->
    <v-dialog v-model="groupDialog" max-width="480">
      <v-card>
        <v-card-title>{{ groupDialogEdit ? 'Edit Group' : 'Add Group' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="groupNameInput"
            label="Group Name"
            density="compact"
            hide-details
            class="mb-4"
          />
          <v-select
            v-model="groupPlacesInput"
            :items="placelist"
            label="Places"
            multiple
            chips
            clearable
            density="compact"
            hide-details
          />
          <p v-if="groupFeedback" class="text-caption text-error mt-2">{{ groupFeedback }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="groupDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="groupSaving" :disabled="!groupNameInput.trim()" @click="saveGroup">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Block confirmation dialog -->
    <v-dialog v-model="blockDialog" max-width="400">
      <v-card>
        <v-card-title>Remove access?</v-card-title>
        <v-card-text>
          Remove <strong>{{ blockTarget }}</strong> from your list? They will need to re-add you to grant access again.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="blockDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="tonal" :loading="blockSaving" @click="executeBlock">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useUserStore } from '@/stores/users';
import { usePlaceStore } from '@/stores/places';
import { getPeopleGrantedAccess, removeRunnerFromUserEnterers, resolveUidsToAliases, lookupUserByAlias } from '@/api/users';

const userStore = useUserStore();
const placeStore = usePlaceStore();

const username = computed(() => userStore.username);
const useremail = computed(() => userStore.useremail);
const admin = computed(() => userStore.admin);
const favoriteplaces = computed(() => userStore.favoriteplaces);
const favoritegames = computed(() => userStore.favoritegames);
const hasPokerPermission = computed(() => userStore.hasPokerPermission);
const placeGroups = computed(() => userStore.placeGroups);
const placelist = computed(() => placeStore.placelist);

// Place group state
const groupDialog = ref(false);
const groupDialogEdit = ref(false);
const groupDialogIndex = ref(-1);
const groupNameInput = ref('');
const groupPlacesInput = ref([]);
const groupSaving = ref(false);
const groupFeedback = ref('');
const deleteGroupIndex = ref(-1);

// Alias edit state
const editingAlias = ref(false);
const aliasInput = ref('');
const aliasSaving = ref(false);
const aliasFeedback = ref('');
const aliasFeedbackError = ref(false);

function startEditAlias() {
  aliasInput.value = username.value ?? '';
  aliasFeedback.value = '';
  aliasFeedbackError.value = false;
  editingAlias.value = true;
}

function cancelEditAlias() {
  editingAlias.value = false;
  aliasFeedback.value = '';
}

async function saveAlias() {
  const trimmed = aliasInput.value.trim();
  aliasFeedback.value = '';
  aliasFeedbackError.value = false;
  if (!trimmed) {
    aliasFeedbackError.value = true;
    aliasFeedback.value = 'Alias cannot be empty.';
    return;
  }
  if (!/^[a-zA-Z0-9]+$/.test(trimmed)) {
    aliasFeedbackError.value = true;
    aliasFeedback.value = 'Alias may only contain letters and numbers.';
    return;
  }
  if (trimmed.toLowerCase() === (username.value ?? '').toLowerCase()) {
    aliasFeedbackError.value = true;
    aliasFeedback.value = 'That is already your alias.';
    return;
  }
  aliasSaving.value = true;
  try {
    const existing = await lookupUserByAlias(trimmed);
    if (existing) {
      aliasFeedbackError.value = true;
      aliasFeedback.value = 'That alias is already taken.';
      return;
    }
    await userStore.updateAlias(trimmed);
    editingAlias.value = false;
    aliasFeedback.value = 'Alias updated!';
    setTimeout(() => { aliasFeedback.value = ''; }, 2500);
  } catch (e) {
    console.error('Error updating alias:', e);
    aliasFeedbackError.value = true;
    aliasFeedback.value = 'Error updating alias. Please try again.';
  } finally {
    aliasSaving.value = false;
  }
}

const resolvedEnterers = ref([]); // [{ uid, alias }] — display list
const newEntererAlias = ref('');
const entererFeedback = ref('');
const entererFeedbackError = ref(false);
const saving = ref(false);

const newEntererAliasTrimmed = computed(() => (newEntererAlias.value || '').trim());

// Granted access state
const grantedAccess = ref([]);
const grantedAccessLoading = ref(false);
const blockDialog = ref(false);
const blockTarget = ref(null);    // alias — for display in dialog
const blockTargetUid = ref(null); // uid — for API call
const blockSaving = ref(false);

async function loadGrantedAccess() {
  const uid = userStore.userid;
  if (!uid) return;
  grantedAccessLoading.value = true;
  grantedAccess.value = await getPeopleGrantedAccess(uid);
  grantedAccessLoading.value = false;
}

async function loadResolvedEnterers() {
  const uids = userStore.allowedTournamentEnterers;
  resolvedEnterers.value = uids.length ? await resolveUidsToAliases(uids) : [];
}

function confirmBlock(person) {
  blockTarget.value = person.alias;
  blockTargetUid.value = person.user_id;
  blockDialog.value = true;
}

async function executeBlock() {
  if (!blockTargetUid.value || blockSaving.value) return;
  blockSaving.value = true;
  try {
    await removeRunnerFromUserEnterers(blockTargetUid.value, userStore.userid);
    grantedAccess.value = grantedAccess.value.filter(p => p.user_id !== blockTargetUid.value);
    blockDialog.value = false;
  } catch (e) {
    console.error('Error removing access:', e);
  }
  blockSaving.value = false;
}

async function addEnterer() {
  const alias = newEntererAliasTrimmed.value;
  if (!alias) return;
  entererFeedback.value = 'Looking up user...';
  entererFeedbackError.value = false;
  const found = await lookupUserByAlias(alias);
  if (!found) {
    entererFeedback.value = `No user found with username "${alias}".`;
    entererFeedbackError.value = true;
    return;
  }
  const current = userStore.allowedTournamentEnterers;
  if (current.includes(found.user_id)) {
    entererFeedback.value = 'That user is already in the list.';
    entererFeedbackError.value = true;
    return;
  }
  persistEnterers([...current, found.user_id]);
  resolvedEnterers.value.push({ uid: found.user_id, alias: found.alias });
  newEntererAlias.value = '';
}

function removeEnterer(uid) {
  entererFeedback.value = '';
  const next = userStore.allowedTournamentEnterers.filter(id => id !== uid);
  persistEnterers(next);
  resolvedEnterers.value = resolvedEnterers.value.filter(r => r.uid !== uid);
}

async function persistEnterers(aliases) {
  if (saving.value) return;
  saving.value = true;
  entererFeedback.value = '';
  entererFeedbackError.value = false;
  try {
    await userStore.updateAllowedTournamentEnterers(aliases);
    entererFeedback.value = 'Saved.';
    setTimeout(() => { entererFeedback.value = ''; }, 2000);
  } catch (e) {
    console.error('Error saving allowed tournament enterers:', e);
    const msg = e?.message || e?.code || String(e);
    entererFeedback.value = msg ? `Failed to save: ${msg}` : 'Failed to save. Try again.';
    entererFeedbackError.value = true;
  }
  saving.value = false;
}

function openAddGroup() {
  groupNameInput.value = '';
  groupPlacesInput.value = [];
  groupDialogEdit.value = false;
  groupDialogIndex.value = -1;
  groupFeedback.value = '';
  groupDialog.value = true;
}

function openEditGroup(i) {
  const g = placeGroups.value[i];
  groupNameInput.value = g.name;
  groupPlacesInput.value = [...g.places];
  groupDialogEdit.value = true;
  groupDialogIndex.value = i;
  groupFeedback.value = '';
  groupDialog.value = true;
}

async function saveGroup() {
  const name = groupNameInput.value.trim();
  if (!name) return;
  if (!groupDialogEdit.value) {
    const duplicate = placeGroups.value.some(g => g.name.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      groupFeedback.value = 'A group with that name already exists.';
      return;
    }
  }
  groupSaving.value = true;
  groupFeedback.value = '';
  try {
    const updated = [...placeGroups.value];
    const entry = { name, places: [...groupPlacesInput.value] };
    if (groupDialogEdit.value) {
      updated[groupDialogIndex.value] = entry;
    } else {
      updated.push(entry);
    }
    await userStore.updatePlaceGroups(updated);
    groupDialog.value = false;
  } catch (e) {
    console.error('Error saving place group:', e);
    groupFeedback.value = 'Error saving. Please try again.';
  } finally {
    groupSaving.value = false;
  }
}

async function deleteGroup() {
  if (deleteGroupIndex.value < 0) return;
  const updated = placeGroups.value.filter((_, i) => i !== deleteGroupIndex.value);
  try {
    await userStore.updatePlaceGroups(updated);
  } catch (e) {
    console.error('Error deleting place group:', e);
  }
  deleteGroupIndex.value = -1;
}

watch(newEntererAlias, () => {
  entererFeedback.value = '';
  entererFeedbackError.value = false;
});

onMounted(() => {
  loadGrantedAccess();
  loadResolvedEnterers();
  placeStore.createPlaceList();
});
</script>
